import { FC, useState } from 'react';

import { AlertTriangle } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import { CartonProduct, DuplicateWarning } from '../../../../types';
import { OfferDraftErrors } from '../__shared/offerDraft';
import { ProductSelector } from '../__shared/ProductSelector';

interface Props {
    selectedProduct: CartonProduct | null;
    onProductSelected: (product: CartonProduct) => void;
    productSearchSeed?: string | null;
    selectedUniverseId: number | null;
    selectedProductTypeId: number | null;
    initialProductOptions: CartonProduct[];
    errors: OfferDraftErrors;
    editing: boolean;
    onCancel: () => void;
}

const firstErrorMessage = (error?: string | string[]) =>
    Array.isArray(error) ? error[0] : error;

export const BulkOfferProductSearch: FC<Props> = ({
    selectedProduct,
    onProductSelected,
    productSearchSeed,
    selectedUniverseId,
    selectedProductTypeId,
    initialProductOptions,
    errors,
    editing,
    onCancel,
}) => {
    const [duplicateWarning, setDuplicateWarning] =
        useState<DuplicateWarning | null>(null);
    const productError = firstErrorMessage(errors.product_id);

    return (
        <div
            className="border-t px-3 py-2.5 sm:px-4"
            data-testid="bulk-offer-product-search"
        >
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium whitespace-nowrap">
                    {editing ? 'Замінити товар:' : 'Додати товар:'}
                </span>
                <div className="min-w-[200px] flex-1">
                    <ProductSelector
                        setData={() => undefined}
                        setSelected={(product) => {
                            setDuplicateWarning(null);
                            onProductSelected(product);
                        }}
                        initialOptions={initialProductOptions}
                        selectedProduct={selectedProduct}
                        searchSeed={productSearchSeed}
                        selectedUniverseId={selectedUniverseId}
                        selectedProductTypeId={selectedProductTypeId}
                        onDuplicateCheck={setDuplicateWarning}
                        placeholder="Введіть назву товару..."
                    />
                </div>
                {editing && (
                    <Button
                        type="button"
                        variant="ghost"
                        className="h-9 shrink-0"
                        onClick={onCancel}
                    >
                        Скасувати
                    </Button>
                )}
            </div>

            {productError && (
                <p className="mt-1.5 text-xs leading-snug text-red-600">
                    {productError}
                </p>
            )}

            {duplicateWarning && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-amber-600">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>{duplicateWarning.message}</span>
                    {duplicateWarning.edit_url && (
                        <a
                            href={duplicateWarning.edit_url}
                            className="font-medium text-primary hover:underline"
                        >
                            Редагувати існуючу
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};
